import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import ChannelsList from "./pages/ChannelsList";
import ChatDetail from "./pages/ChatDetail";
import AdminSettings from "./pages/AdminSettings";
import EventsPage from "./pages/EventsPage";
import EventsDashboard from "./pages/EventsDashboard";
import HotelManagement from "./pages/HotelManagement";
import ChecklistPage from "./pages/ChecklistPage";
import ChatPage from "./pages/ChatPage";
import DashboardPage from "./pages/DashboardPage";
import PendingChecklistGate from "./components/PendingChecklistGate";
import AppShell from "./components/AppShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, hotel) => {
    const response = await axios.post(`${API}/auth/login`, { email, password, hotel });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-pulse text-stone-400">Caricamento...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// App Layout - conditional width for landing vs app pages
const AppLayout = ({ children }) => {
  return children;
};

// Inner app layout with mobile constraint
const MobileLayout = ({ children }) => {
  return (
    <AppShell>
      {children}
      <PendingChecklistGate />
    </AppShell>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppLayout>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MobileLayout><ChannelsList /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:channelId"
              element={
                <ProtectedRoute>
                  <MobileLayout><ChatDetail /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <MobileLayout><AdminSettings /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute>
                  <MobileLayout><EventsPage /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/dashboard"
              element={
                <ProtectedRoute>
                  <MobileLayout><EventsDashboard /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hotels"
              element={
                <ProtectedRoute>
                  <MobileLayout><HotelManagement /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist"
              element={
                <ProtectedRoute>
                  <MobileLayout><ChecklistPage /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <MobileLayout><ChatPage /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <MobileLayout><DashboardPage /></MobileLayout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppLayout>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API };
